
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ReplenishmentSettingHelper } from '@/helpers/ReplenishmentSettingHelper';
import { FilterColumnList } from '@/collections/filter_columns';

interface TableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
  search?: string;
  filters?: FilterColumnList;
}
interface ScopeFilter {
  id: number | null;
  operator: 'eq' | 'ne' | 'all' | 'null';
  scope: 'supplier' | 'location';
}

@Component
export default class ProductVariantTable extends Vue {


  private get selectedProducts(): any[] {
    return this.value;
  }

  private set selectedProducts(val: any[]) {
    this.$emit('input', val);
  }

  private get areAllCurrentPageItemsSelected(): boolean {
    if (!this.products || this.products.length === 0) return false;
    const idField = this.scope === 'product' ? 'product_id' : 'variant_id';
    return this.products.every(product => 
      this.selectedProducts.some(selected => selected[idField] === product[idField])
    );
  }

  private get areSomeCurrentPageItemsSelected(): boolean {
    if (!this.products || this.products.length === 0) return false;
    const idField = this.scope === 'product' ? 'product_id' : 'variant_id';
    return this.products.some(product => 
      this.selectedProducts.some(selected => selected[idField] === product[idField])
    );
  }

  @Prop({ default: '' })
  public search!: string;

  @Prop({ default: null })
  public supplierId!: number | null;

  @Prop({ default: null })
  public locationId!: number | null;

  @Prop({ default: () => [] })
  public value!: any[];

  @Prop({ default: 'variant' })
  public scope!: 'product' | 'variant';

  @Prop({ default: 'supplier' })
  public scopeType!: 'supplier' | 'location';

  @Prop({ default: () => new FilterColumnList() })
  public filters!: FilterColumnList;

  @Prop({ default: false })
  public isSelectAll!: boolean;

  private products: any[] = [];
  private loading = false;
  private totalItems = 0;
  private tableOptions: TableOptions = {
    page: 1,
    itemsPerPage: 20,
    sortBy: [],
    sortDesc: [],
  };

  private headers = [
    { text: '', value: 'data-table-select', width: '40px' },
    { text: 'Product', value: 'product', width: '180px'  },
    { text: 'SKU', value: 'product_sku', width: '90px', class: 'hidden-sm-and-down' },
    { text: 'Supplier', value: 'supplier', width: '90px', class: 'hidden-xs-only' },
    { text: 'Reorder Parameters', value: 'reorder_parameters', width: '220px' },
    { text: 'Inventory Parameters', value: 'inventory_parameters', width: '200px', class: 'hidden-md-and-down' },
    { text: 'Scope', value: 'product_scope', width: '60px', class: 'hidden-sm-and-down' },
    { text: 'Status', value: 'replenishment_status', width: '80px' },
    // { text: 'Actions', value: 'actions', width: '100px', sortable: false },
  ];

  @Watch('locationId')
  @Watch('supplierId')
  @Watch('filters', { deep: true })
  @Watch('search')
  async onFilterChange() {
    this.tableOptions.page = 1; // Reset to first page
    await this.fetchProducts();
  }

  @Watch('isSelectAll')
  onSelectAllChange(newVal: boolean) {
    // When Select All is activated, select all current products
    if (newVal && this.products && this.products.length > 0) {
      this.$emit('input', [...this.products]);
    } else if (!newVal) {
      this.$emit('input', []);
    }
  }

  private formatMinMax(min: number | undefined, max: number | undefined): string {
    if (min === undefined || max === undefined) { return 'Not Configured'; }
    return `${min}/ ${max > 0 ? max : '-'}`;
  }

  private async onOptionsUpdate(options: TableOptions) {
    this.tableOptions = { ...options };
    await this.fetchProducts();
  }

  private async fetchProducts() {
    this.loading = true;
    this.products = [];
    try {
      const options = {
        ...this.tableOptions,
        search: this.search,
        filters: this.filters,
      };

      /** correct the sortby and sortdesc if the sortby is product */
      if (!options.sortBy || options.sortBy.length === 0) {
        options.sortBy = ['Item[Item Description]', 'variant'];
        options.sortDesc = [false, false];
      } else if (options.sortBy[0] === 'product') {
        options.sortBy = ['Item[Item Description]', 'variant'];
        options.sortDesc = [options.sortDesc[0], false];
      }

      const scopeFilter: ScopeFilter = {
        id: this.scopeType === 'supplier' ? this.supplierId : this.locationId,
        operator: this.supplierId || this.locationId ? 'eq' : 'all',
        scope: this.scopeType,
      };

      const response = this.scope === 'product'
        ? await ReplenishmentSettingHelper.fetchProductsWithPagination(options, scopeFilter)
        : await ReplenishmentSettingHelper.fetchVariantsWithPagination(options, scopeFilter);

      this.products = response.items;
      this.totalItems = response.total;
      this.$emit('update:totalItems', this.totalItems);
    } catch (error) {
      // Error fetching products
      this.$emit('error', 'Failed to load products');
    } finally {
      this.loading = false;
    }
  }

  private onSelectionChange(val: any[]) {
    this.selectedProducts = val;
  }

  private toggleSelectCurrentPage() {
    if (this.isSelectAll) return; // Don't allow toggle when Select All is active

    const idField = this.scope === 'product' ? 'product_id' : 'variant_id';
    
    if (this.areAllCurrentPageItemsSelected) {
      // Deselect all current page items
      const currentPageIds = new Set(this.products.map(p => p[idField]));
      const newSelection = this.selectedProducts.filter(item => 
        !currentPageIds.has(item[idField])
      );
      this.$emit('input', newSelection);
    } else {
      // Select all current page items
      const selectedIds = new Set(this.selectedProducts.map(item => item[idField]));
      const itemsToAdd = this.products.filter(product => 
        !selectedIds.has(product[idField])
      );
      this.$emit('input', [...this.selectedProducts, ...itemsToAdd]);
    }
  }
}
